import React, { useEffect, useRef, useState } from "react";
import { Button } from "@penmate/react-shared/ui/button";
import type { Candidate } from "../api/searches";
import { callName, displayName, recordName } from "./names";

// "Listed as …" only when the record's form differs by more than case or
// spacing — a reorder or extra content is worth showing, shouting is not.
const normalizeName = (s: string): string => s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();
const showListedAs = (c: Candidate): boolean =>
  !!c.name && normalizeName(displayName(c)) !== normalizeName(c.name);

const Detail = ({ label, value }: { label: string; value: string | null }) => {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-3 border-b border-border py-2 text-sm last:border-b-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
};

// Candidates arrive ranked best-first (the runtime sorts by name agreement),
// so index 0 is the right opening card. Detail shown is the source's reported
// data — never the resolved facility's mailing address.
export const MatchCard = ({
  firstName,
  candidates,
  sourceLabel,
  onDeclinedAll,
  onConfirm,
  confirmPending,
  confirmFailed,
  onEscape,
  initialRejected,
  onReject,
}: {
  // The person the user is looking for — the card asks about them by name.
  firstName: string;
  candidates: Candidate[];
  sourceLabel: string;
  onDeclinedAll: () => void;
  onConfirm: (candidate: Candidate) => void;
  confirmPending: boolean;
  confirmFailed: boolean;
  onEscape: () => void;
  // Candidates rejected on an earlier visit to this card, and where new
  // rejections are recorded, so Back/forward never shows a rejected person again.
  initialRejected?: number[];
  onReject?: (rejected: number[]) => void;
}) => {
  const [rejected, setRejected] = useState<number[]>(initialRejected || []);
  const [index, setIndex] = useState(() => {
    const start = initialRejected || [];
    for (let i = 0; i < candidates.length; i++) if (start.indexOf(i) === -1) return i;
    return 0;
  });
  const [showOthers, setShowOthers] = useState(false);
  // A card the user chose from the list is theirs, not our proposal: the
  // screen then follows the card's name instead of asking about the person
  // they typed. "Not them" hands the next proposal back to us.
  const [picked, setPicked] = useState(false);
  // The failure belongs to the candidate whose confirm failed — switching to a
  // different candidate must not open on "Try again", and a card mounted while
  // an older failure still stands never inherits it.
  const [failedIndex, setFailedIndex] = useState<number | null>(null);
  const wasFailed = useRef(confirmFailed);
  useEffect(() => {
    if (confirmFailed && !wasFailed.current) setFailedIndex(index);
    wasFailed.current = confirmFailed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmFailed]);
  const showFailure = confirmFailed && failedIndex === index;
  const candidate = candidates[index];
  const others = candidates.length - 1;
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const othersId = "pm-match-others";

  if (!candidate) return null;

  // "Not them" moves to the next candidate the user hasn't rejected — the
  // "see more" list allows jumping around, so plain index+1 could skip
  // unreviewed people straight to the declined exit.
  const notThem = () => {
    const nowRejected = rejected.indexOf(index) === -1 ? rejected.concat(index) : rejected;
    setRejected(nowRejected);
    if (onReject) onReject(nowRejected);
    for (let i = 0; i < candidates.length; i++) {
      if (nowRejected.indexOf(i) === -1) {
        setIndex(i);
        setShowOthers(false);
        setPicked(false);
        return;
      }
    }
    onDeclinedAll();
  };
  const record = recordName(candidate);
  const subject = picked && record ? callName(record.first) : firstName;

  return (
    <div className="flex w-full flex-col gap-4" aria-label="Possible match">
      {/* This card persists across candidate changes, so updates here are
          announced — a focus-parked screen reader still hears who is shown. */}
      <span className="sr-only" role="status" aria-live="polite">
        Possible match: {candidate.name}
      </span>
      <div>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-[28px] font-extrabold leading-[1.15] tracking-[-0.02em] text-foreground focus:outline-none"
        >
          {picked ? "Is this the right person?" : <>Is this {firstName}?</>}
        </h1>
        <p className="mt-2 text-base leading-relaxed text-muted-foreground">
          We found a match in {sourceLabel}. Make sure the details look right.
        </p>
      </div>

      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <div className="h-1.5 w-full bg-[hsl(var(--teal-bright))]" aria-hidden="true" />
        <div className="p-5">
          <div className="text-xl font-bold text-foreground">{displayName(candidate)}</div>
          {showListedAs(candidate) && (
            <div className="mt-0.5 text-xs text-muted-foreground">Listed as {candidate.name}</div>
          )}
          <div className="mt-3">
            <Detail label="Facility" value={candidate.facility} />
            <Detail label="ID number" value={candidate.inmateId} />
            <Detail label="Age" value={candidate.age} />
          </div>
        </div>
        {/* The card's last line is the user's next step, never our own work:
            the address behind this record is already resolved by now. */}
        <div className="border-t border-border bg-secondary/60 px-5 py-3 text-sm leading-snug text-muted-foreground">
          Confirm to connect with {subject} and start sending messages and photos.
        </div>
        {others > 0 && (
          <div className="border-t border-border">
            <button
              type="button"
              className="flex min-h-[48px] w-full items-center justify-between px-5 text-[15px] font-semibold text-primary"
              onClick={() => setShowOthers(!showOthers)}
              disabled={confirmPending}
              aria-expanded={showOthers}
              aria-controls={othersId}
            >
              <span>
                {showOthers ? "Hide" : "See"} {others} more possible {others === 1 ? "match" : "matches"}
              </span>
              <svg
                className={showOthers ? "h-5 w-5 rotate-90" : "h-5 w-5"}
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path d="M7 5l6 5-6 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {showOthers && (
              <ul id={othersId} className="border-t border-border" aria-label="Other possible matches">
              {candidates.map((c, i) =>
                i === index ? null : (
                  <li key={`${c.name}-${i}`} className="border-b border-border last:border-b-0">
                    <button
                      type="button"
                      className="w-full px-4 py-3 text-left text-sm hover:bg-accent"
                      disabled={confirmPending}
                      onClick={() => {
                        setIndex(i);
                        setShowOthers(false);
                        setPicked(true);
                        // The row unmounts under the finger; park focus on the card.
                        if (headingRef.current) headingRef.current.focus();
                      }}
                    >
                      <span className="font-medium">
                        {displayName(c)}
                        {c.age ? ` (${c.age})` : ""}
                      </span>
                      {c.facility && (
                        <span className="block text-muted-foreground">{c.facility}</span>
                      )}
                    </button>
                  </li>
                ),
              )}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {showFailure && (
          <p className="text-sm font-medium text-destructive" role="alert">
            We couldn&rsquo;t add them just now. Try again.
          </p>
        )}
        <Button
          size="lg"
          className="h-[52px] w-full rounded-xl text-[17px] font-semibold"
          disabled={confirmPending}
          onClick={() => onConfirm(candidate)}
        >
          {confirmPending ? `Adding ${subject}…` : showFailure ? "Try again" : `Yes, connect with ${subject}`}
        </Button>
        <button
          type="button"
          className="min-h-[44px] text-[15px] font-semibold text-primary"
          onClick={notThem}
          disabled={confirmPending}
        >
          Not them
        </button>
        {showFailure && (
          <button
            type="button"
            className="min-h-[44px] text-[15px] font-semibold text-primary"
            onClick={onEscape}
          >
            Skip for now — go to My Penmates
          </button>
        )}
      </div>

    </div>
  );
};
